<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { SvelteSet } from 'svelte/reactivity';
  import { Pencil } from 'lucide-svelte';
  import { hrUsersApi, hrWorkZonesApi } from '$lib/api/hr';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import DataTable from '$lib/components/ui/DataTable.svelte';
  import type { HRUser } from '$lib/types';

  const queryClient = useQueryClient();
  const usersQuery = createQuery(() => ({
    queryKey: ['hr-users'],
    queryFn: () => hrUsersApi.list()
  }));
  const sitesQuery = createQuery(() => ({
    queryKey: ['hr-work-zones'],
    queryFn: () => hrWorkZonesApi.list()
  }));

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['hr-users'] });
  }

  let editing = $state<HRUser | null>(null);
  const selected = new SvelteSet<number>();
  let saving = $state(false);

  function openEdit(u: HRUser) {
    editing = u;
    selected.clear();
    u.work_zone_ids.forEach((id) => selected.add(id));
  }

  function toggle(id: number) {
    if (selected.has(id)) selected.delete(id);
    else selected.add(id);
  }

  async function save() {
    if (!editing) return;
    saving = true;
    try {
      await hrUsersApi.updateWorkZones(editing.id, [...selected]);
      refresh();
      editing = null;
      toast.success('Site assignments updated.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      saving = false;
    }
  }
</script>

<div class="tym-page">
  <p class="tym-section-sub mb-4">Which sites can each user punch in from.</p>

  {#if usersQuery.isLoading}
    <div class="tym-loading-state">Loading...</div>
  {:else if usersQuery.isError}
    <div class="tym-error-state">Could not load users.</div>
  {:else if usersQuery.data}
    <DataTable
      mobileCards
      columns={[
        { key: 'user', label: 'User', cell: userCell },
        { key: 'remote', label: 'Punch', cell: remoteCell },
        { key: 'sites', label: 'Assigned sites', cell: sitesCell },
        { key: 'actions', label: '', align: 'right' as const, cell: actionsCell }
      ]}
      rows={usersQuery.data}
      rowKey={(u) => u.id}
      emptyTitle="No users found"
      emptyBody="Create a user in Directory first."
    />
  {/if}

  {#if editing}
    <Modal title={`Assign sites — ${editing.name || editing.email}`} onClose={() => (editing = null)}>
      <div class="space-y-4">
        {#if sitesQuery.isLoading}
          <div class="tym-loading-state">Loading sites...</div>
        {:else if sitesQuery.data}
          <div class="space-y-1.5">
            {#each sitesQuery.data as site (site.id)}
              <label class="flex items-center gap-2 text-[13px] text-toyama-heavy-carbon">
                <input
                  type="checkbox"
                  checked={selected.has(site.id)}
                  onchange={() => toggle(site.id)}
                  class="h-4 w-4 accent-toyama-teal"
                />
                {site.name}
                {#if site.all_users}<span class="tym-badge">all users</span>{/if}
              </label>
            {:else}
              <p class="text-[13px] text-dark-gray">No sites yet — create one first.</p>
            {/each}
          </div>
        {/if}
        <div class="flex justify-end gap-2 pt-1">
          <button type="button" class="btn-secondary" onclick={() => (editing = null)}>Cancel</button>
          <button type="button" class="btn-primary" disabled={saving} onclick={save}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  {/if}
</div>

{#snippet userCell(u: HRUser)}
  <span class="font-medium text-navy">{u.name || u.email}</span>
{/snippet}
{#snippet remoteCell(u: HRUser)}
  <span class="muted">{u.punch_mode === 'field' ? 'Hotel field technician' : u.punch_mode === 'untagged' ? 'Sales' : u.punch_mode === 'home' ? 'Home or office' : 'On site'}</span>
{/snippet}
{#snippet sitesCell(u: HRUser)}
  <span class="muted">{u.work_zone_names.join(', ') || '—'}</span>
{/snippet}
{#snippet actionsCell(u: HRUser)}
  <div class="flex justify-end">
    <button type="button" class="btn-secondary" onclick={() => openEdit(u)}>
      <Pencil class="h-3.5 w-3.5" /> Edit
    </button>
  </div>
{/snippet}
